/**
 * The writes of Tarifs et abonnements, one per intent: a price version, a
 * promo, a discount, and Finance's two moves on a subscription. Each is
 * offered only with its own staff feature (the server checks again), names
 * the `version` on screen where there is one, gives a reason, and always
 * carries the intent's Idempotency-Key: these writes refuse to run without
 * one (ADR-0016 §2.1, ADR-0018 §4a).
 */
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type {
  ConsoleSubscription,
  DiscountScope,
  PlanPriceVersion,
  PlanPromo,
  ProDiscount,
} from "@wallyo/contracts/console/billing";
import type { ConsoleClient } from "../../api/client";
import {
  createDiscount,
  createPromo,
  endDiscount,
  endPromo,
  endSubscription,
  extendSubscription,
  setPlanPrice,
} from "../../api/console-api";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { t, type MessageId } from "../../i18n";
import { formatDateTime, formatMoney } from "../../i18n/format";
import { fromLocalInput } from "../local-datetime";
import { parseAmount, REASON_MAX, REASON_MIN } from "../payments/payment-actions";
import type { WriteFailure } from "../team/team-format";

const f = (name: string) => `${name}_STAFF_FEATURE_ENABLEMENT` as StaffFeatureId;

export const BILLING_FEATURES = {
  view: f("VIEW_PAYMENTS"),
  prices: f("MANAGE_PLAN_PRICES"),
  promos: f("RUN_PROMOS"),
  discounts: f("OFFER_DISCOUNTS"),
  subscriptions: f("MANAGE_SUBSCRIPTIONS"),
} as const satisfies Record<string, StaffFeatureId>;

/** What a dialog acts on. The `end` ones carry the object whose version is sent. */
export type BillingTarget =
  | { kind: "setPrice" }
  | { kind: "newPromo" }
  | { kind: "newDiscount" }
  | { kind: "endPromo"; promo: PlanPromo }
  | { kind: "endDiscount"; discount: ProDiscount }
  | { kind: "extendSubscription"; subscription: ConsoleSubscription }
  | { kind: "endSubscription"; subscription: ConsoleSubscription };

export type BillingWriteKind = BillingTarget["kind"];

/** What the server answered, as the screen shows it: never what was typed. */
export type BillingWriteResult =
  | { kind: "setPrice"; price: PlanPriceVersion }
  | { kind: "newPromo"; promo: PlanPromo }
  | { kind: "endPromo"; promo: PlanPromo }
  | { kind: "newDiscount"; discount: ProDiscount }
  | { kind: "endDiscount"; discount: ProDiscount }
  | { kind: "extendSubscription"; subscription: ConsoleSubscription }
  | { kind: "endSubscription"; subscription: ConsoleSubscription };

// ── Fields ────────────────────────────────────────────────────────────────

export type BillingFields = {
  feeTierId: string;
  /** ISO 3166-1 alpha-2, as typed. */
  country: string;
  /** ISO 4217, as typed. */
  currency: string;
  amount: string;
  /** A `datetime-local` value, or "" for "starts now". */
  validFrom: string;
  name: string;
  /** "" means for as long as the pro stays subscribed. */
  periods: string;
  startsAt: string;
  endsAt: string;
  scope: DiscountScope;
  workerId: string;
  /** A WorkerCategory, for the `category` scope. */
  category: string;
  percentOff: string;
  paidThrough: string;
  reason: string;
};

export const EMPTY_BILLING_FIELDS: BillingFields = {
  feeTierId: "",
  country: "",
  currency: "",
  amount: "",
  validFrom: "",
  name: "",
  periods: "",
  startsAt: "",
  endsAt: "",
  scope: "pro",
  workerId: "",
  category: "",
  percentOff: "",
  paidThrough: "",
  reason: "",
};

export const NAME_MIN = 2;
export const NAME_MAX = 80;
const COUNTRY = /^[A-Za-z]{2}$/;
const CURRENCY = /^[A-Za-z]{3}$/;

/** "12" → 12; null when it isn't a whole number above zero. */
export function parseCount(text: string): number | null {
  const cleaned = text.trim();
  if (!/^\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (value <= 0) return null;
  return value;
}

function windowProblem(fields: BillingFields): string | null {
  const from = fromLocalInput(fields.startsAt);
  const to = fromLocalInput(fields.endsAt);
  if (!from || !to) return t("billing.problem.window");
  if (to.getTime() <= from.getTime()) return t("billing.problem.windowOrder");
  if (fields.periods.trim() && parseCount(fields.periods) === null) return t("billing.problem.periods");
  return null;
}

function targetProblem(fields: BillingFields): string | null {
  if (fields.scope === "pro") {
    if (!fields.workerId.trim()) return t("billing.problem.pro");
    return null;
  }
  if (fields.scope === "country") {
    if (!COUNTRY.test(fields.country.trim())) return t("billing.problem.country");
    return null;
  }
  if (!fields.category.trim()) return t("billing.problem.category");
  return null;
}

/** The first problem with what was typed, or null when it can be sent. */
export function billingProblem(kind: BillingWriteKind, fields: BillingFields, now: Date = new Date()): string | null {
  const reason = fields.reason.trim().length;
  if (reason < REASON_MIN || reason > REASON_MAX) return t("billing.problem.reason", { min: REASON_MIN });
  switch (kind) {
    case "setPrice": {
      if (!fields.feeTierId) return t("billing.problem.plan");
      if (!COUNTRY.test(fields.country.trim())) return t("billing.problem.country");
      if (!CURRENCY.test(fields.currency.trim())) return t("billing.problem.currency");
      if (parseAmount(fields.amount) === null) return t("billing.problem.amount");
      if (!fields.validFrom.trim()) return null;
      const from = fromLocalInput(fields.validFrom);
      if (!from) return t("billing.problem.validFrom");
      if (from.getTime() < now.getTime()) return t("billing.problem.backdated");
      return null;
    }
    case "newPromo": {
      if (!fields.feeTierId) return t("billing.problem.plan");
      if (!COUNTRY.test(fields.country.trim())) return t("billing.problem.country");
      const name = fields.name.trim().length;
      if (name < NAME_MIN || name > NAME_MAX) return t("billing.problem.name", { min: NAME_MIN });
      if (parseAmount(fields.amount) === null) return t("billing.problem.amount");
      return windowProblem(fields);
    }
    case "newDiscount": {
      const name = fields.name.trim().length;
      if (name < NAME_MIN || name > NAME_MAX) return t("billing.problem.name", { min: NAME_MIN });
      const scoped = targetProblem(fields);
      if (scoped) return scoped;
      const percent = parseCount(fields.percentOff);
      if (percent === null || percent > 100) return t("billing.problem.percentOff");
      return windowProblem(fields);
    }
    case "extendSubscription": {
      const until = fromLocalInput(fields.paidThrough);
      if (!until) return t("billing.problem.paidThrough");
      if (until.getTime() <= now.getTime()) return t("billing.problem.paidThroughPast");
      return null;
    }
    default:
      return null;
  }
}

// ── Sending ───────────────────────────────────────────────────────────────

const iso = (value: string): string => fromLocalInput(value)!.toISOString();

/** "" means no limit: the reduction lasts while the pro stays subscribed. */
function periodsOf(fields: BillingFields): number | null {
  if (!fields.periods.trim()) return null;
  return parseCount(fields.periods);
}

/** Left out means "starts now"; the server never accepts a date in the past. */
function validFromOf(fields: BillingFields): { validFrom?: string } {
  if (!fields.validFrom.trim()) return {};
  return { validFrom: iso(fields.validFrom) };
}

/** Exactly one target, matching the scope: the other two are sent as null. */
function discountTargetOf(fields: BillingFields): {
  workerId: string | null;
  country: string | null;
  category: string | null;
} {
  const empty = { workerId: null, country: null, category: null };
  if (fields.scope === "pro") return { ...empty, workerId: fields.workerId.trim() };
  if (fields.scope === "country") return { ...empty, country: fields.country.trim().toUpperCase() };
  return { ...empty, category: fields.category };
}

/** Sends one write and answers with what the server sent back, never with what was typed. */
export async function sendBillingWrite(
  client: ConsoleClient,
  target: BillingTarget,
  fields: BillingFields,
  idempotencyKey: string,
): Promise<BillingWriteResult> {
  const options = { idempotencyKey };
  const reason = fields.reason.trim();
  const periods = periodsOf(fields);
  switch (target.kind) {
    case "setPrice": {
      const { price } = await setPlanPrice(
        client,
        {
          feeTierId: fields.feeTierId,
          country: fields.country.trim().toUpperCase(),
          currency: fields.currency.trim().toUpperCase(),
          amount: parseAmount(fields.amount) ?? 0,
          ...validFromOf(fields),
          reason,
        },
        options,
      );
      return { kind: "setPrice", price };
    }
    case "newPromo": {
      const { promo } = await createPromo(
        client,
        {
          feeTierId: fields.feeTierId,
          country: fields.country.trim().toUpperCase(),
          name: fields.name.trim(),
          amount: parseAmount(fields.amount) ?? 0,
          periods,
          startsAt: iso(fields.startsAt),
          endsAt: iso(fields.endsAt),
          reason,
        },
        options,
      );
      return { kind: "newPromo", promo };
    }
    case "endPromo": {
      const { promo } = await endPromo(
        client,
        target.promo.planPromoId,
        { reason, version: target.promo.version },
        options,
      );
      return { kind: "endPromo", promo };
    }
    case "newDiscount": {
      const { discount } = await createDiscount(
        client,
        {
          name: fields.name.trim(),
          scope: fields.scope,
          ...discountTargetOf(fields),
          feeTierId: fields.feeTierId || null,
          percentOff: parseCount(fields.percentOff) ?? 0,
          periods,
          startsAt: iso(fields.startsAt),
          endsAt: iso(fields.endsAt),
          reason,
        },
        options,
      );
      return { kind: "newDiscount", discount };
    }
    case "endDiscount": {
      const { discount } = await endDiscount(
        client,
        target.discount.proDiscountId,
        { reason, version: target.discount.version },
        options,
      );
      return { kind: "endDiscount", discount };
    }
    case "extendSubscription": {
      const { subscription } = await extendSubscription(
        client,
        target.subscription.subscriptionId,
        { paidThrough: iso(fields.paidThrough), reason, version: target.subscription.version },
        options,
      );
      return { kind: "extendSubscription", subscription };
    }
    case "endSubscription": {
      const { subscription } = await endSubscription(
        client,
        target.subscription.subscriptionId,
        { reason, version: target.subscription.version },
        options,
      );
      return { kind: "endSubscription", subscription };
    }
  }
}

/** The server's confirmation of a write, in words. */
export type BillingOutcome = { text: string };

/** What the server did, from its own answer: never what was typed. */
export function billingOutcome(result: BillingWriteResult): BillingOutcome {
  switch (result.kind) {
    case "setPrice":
      return {
        text: t("billing.outcome.price", {
          amount: formatMoney(result.price.amount, result.price.currency),
          date: formatDateTime(result.price.validFrom),
        }),
      };
    case "newPromo":
      return { text: t("billing.outcome.promoCreated", { name: result.promo.name }) };
    case "endPromo":
      return { text: t("billing.outcome.promoEnded", { name: result.promo.name }) };
    case "newDiscount":
      return { text: t("billing.outcome.discountCreated", { name: result.discount.name }) };
    case "endDiscount":
      return { text: t("billing.outcome.discountEnded", { name: result.discount.name }) };
    case "extendSubscription":
      return { text: t("billing.outcome.extended", { date: paidThroughOf(result.subscription) }) };
    case "endSubscription":
      return { text: t("billing.outcome.subscriptionEnded") };
  }
}

function paidThroughOf(subscription: ConsoleSubscription): string {
  if (!subscription.paidThrough) return "—";
  return formatDateTime(subscription.paidThrough);
}

// ── Refusals ──────────────────────────────────────────────────────────────

/**
 * The server's reasons for refusing a billing write, in the staff member's
 * words. `stale`: what was on screen has moved on, so offer a reload.
 */
const REFUSALS: Readonly<Record<string, { message: MessageId; stale: boolean }>> = {
  stale_version: { message: "billing.error.stale", stale: true },
  plan_not_priced: { message: "billing.refused.planNotPriced", stale: false },
  invalid_amount: { message: "billing.refused.invalidAmount", stale: false },
  backdated: { message: "billing.refused.backdated", stale: false },
  price_version_exists: { message: "billing.refused.priceVersionExists", stale: true },
  invalid_window: { message: "billing.refused.invalidWindow", stale: false },
  already_over: { message: "billing.refused.alreadyOver", stale: true },
  feature_not_enabled: { message: "billing.error.refused", stale: false },
  idempotency_key_required: { message: "billing.refused.idempotencyKeyRequired", stale: false },
};

/**
 * A refused billing write (ADR-0016 §2.5): worded from the server's machine
 * `reason` when this knows it, otherwise from its code. Never the server's
 * English text; always the request ID.
 */
export function describeBillingFailure(error: unknown): WriteFailure {
  const { message, reference } = describeError(error);
  if (!(error instanceof ConsoleApiError)) return { message, reference, stale: false };
  const refusal = REFUSALS[error.reason ?? ""];
  if (refusal) return { message: t(refusal.message), reference, stale: refusal.stale };
  switch (error.code) {
    case "stale_version":
      return { message: t("billing.error.stale"), reference, stale: true };
    case "feature_not_enabled":
      return { message: t("billing.error.refused"), reference, stale: false };
    case "validation_error":
      return { message: t("billing.error.invalid"), reference, stale: true };
    case "network_error":
      if (!error.outcomeUnknown) return { message, reference, stale: false };
      return { message: t("billing.error.outcomeUnknown"), reference, stale: false };
    default:
      return { message, reference, stale: false };
  }
}
